/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Component, regionPropType} from '@salesforce/commerce-sdk-react/components'
import {OrangeArrow} from '../../../components/icons'
/**
 * Display child components in a carousel slider manner. Configurations include the number of
 * children to display in view as well as whether or not to show controls and position indicators.
 *
 * @param {PageProps} props
 * @param {string} props.textHeading - Heading text for the carousel.
 * @param {boolean} props.xsCarouselIndicators - Show/Hide carousel indicators/pips on "xs" screens.
 * @param {boolean} props.smCarouselIndicators - Show/Hide carousel indicators/pips on "sm" screens.
 * @param {boolean} props.mdCarouselIndicators - Show/Hide carousel indicators/pips on "md" screens.
 * @param {boolean} props.xsCarouselControls - Show/Hide carousel forward/back controls on "xs" screens.
 * @param {boolean} props.smCarouselControls - Show/Hide carousel forward/back controls on "sm" screens.
 * @param {number} props.xsCarouselSlidesToDisplay - Number of children that will be rendered in view on "xs" screens.
 * @param {number} props.smCarouselSlidesToDisplay - Number of children that will be rendered in view on "sm" screens.
 * @param {number} props.mdCarouselSlidesToDisplay - Number of children that will be rendered in view on "md" screens.
 * @param {Object []} props.region - The regions passed internally to this component by the `commerce-sdk-react` Page component.
 * @returns {React.ReactElement} - Carousel component.
 */
export const BannerCarousel = (props = {}) => {
    const {regions} = props
    const sliderRef = useRef(null)

    const components = regions[0]?.components || []

    // Custom Slider Styling
    const sliderCustomStyle = {
        '.slick-arrow': {
            zIndex: '1',
            position: 'absolute',
            top: '50%'
        },
        '.slick-slider .slick-next': {
            right: '0'
        },
        '.slick-slider .slick-prev': {
            left: '30px'
        },
        '.slick-next:before, .slick-prev:before': {
            content: 'none'
        },
        '.slick-track': {
            display: 'flex'
        }
    }

    const SampleNextArrow = (props) => {
        const {className, onClick} = props
        return (
            <Box
                className={className}
                onClick={onClick}
                tabIndex={0}
                role="button"
                aria-label="Next Slide"
                onKeyDown={(e) => e.key === 'Enter' && onClick()}
            >
                <OrangeArrow />
            </Box>
        )
    }

    function SamplePrevArrow(props) {
        const {className, onClick} = props
        return (
            <Box
                className={className}
                onClick={onClick}
                tabIndex={0}
                role="button"
                aria-label="Previous Slide"
                onKeyDown={(e) => e.key === 'Enter' && onClick()}
            >
                <OrangeArrow />
            </Box>
        )
    }

    // Function to handle accessibility for carousel slides
    const handleAccessibility = () => {
        if (!sliderRef.current) return

        // Find all slides
        const allSlides = sliderRef.current.querySelectorAll('.slick-slide')

        allSlides.forEach((slide) => {
            const isHidden = slide.getAttribute('aria-hidden') === 'true'
            const focusableElements = slide.querySelectorAll(
                'a, button, [tabindex]:not([tabindex="-1"])'
            )

            focusableElements.forEach((element) => {
                if (isHidden) {
                    // For hidden slides, make elements unfocusable
                    element.setAttribute('tabindex', '-1')
                    // Save original tabindex if needed for future restoration
                    if (
                        !element.hasAttribute('data-original-tabindex') &&
                        element.hasAttribute('tabindex')
                    ) {
                        element.setAttribute(
                            'data-original-tabindex',
                            element.getAttribute('tabindex')
                        )
                    }
                } else {
                    // For visible slides, restore focusability
                    if (element.hasAttribute('data-original-tabindex')) {
                        const originalTabIndex = element.getAttribute('data-original-tabindex')
                        element.setAttribute('tabindex', originalTabIndex)
                    } else {
                        element.removeAttribute('tabindex')
                    }
                }
            })
        })
    }

    // Handle slide change events
    const handleAfterChange = () => {
        handleAccessibility()
    }

    // Set up after component mounts
    useEffect(() => {
        // Initial accessibility check
        handleAccessibility()

        // Set up mutation observer to detect when slides change visibility
        const observer = new MutationObserver(handleAccessibility)

        if (sliderRef.current) {
            const slideContainer = sliderRef.current.querySelector('.slick-track')
            if (slideContainer) {
                observer.observe(slideContainer, {
                    attributes: true,
                    attributeFilter: ['aria-hidden'],
                    subtree: true,
                    childList: true
                })
            }
        }

        return () => observer.disconnect()
    }, [])

    const settings = {
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        afterChange: handleAfterChange,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 640,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            }
        ]
    }

    return (
        <Box
            className={'carousel'}
            maxWidth={{base: '100%', xl: '1920px'}}
            margin={`auto`}
            position="relative"
            data-testid="carousel"
            mb={2}
            ref={sliderRef}
        >
            <Box
                width="100%"
                height="100%"
                overflow={'hidden'}
                sx={sliderCustomStyle}
                className="slider-container"
            >
                <Slider {...settings}>
                    {components.map((component, index) => (
                        <Box key={index} data-testid="carousel">
                            <Component component={component} />
                        </Box>
                    ))}
                </Slider>
            </Box>
        </Box>
    )
}

BannerCarousel.propTypes = {
    regions: PropTypes.arrayOf(regionPropType).isRequired,
    textHeadline: PropTypes.string,
    xsCarouselIndicators: PropTypes.bool,
    smCarouselIndicators: PropTypes.bool,
    mdCarouselIndicators: PropTypes.bool,
    xsCarouselControls: PropTypes.bool,
    smCarouselControls: PropTypes.bool,
    xsCarouselSlidesToDisplay: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    smCarouselSlidesToDisplay: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    mdCarouselSlidesToDisplay: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    className: PropTypes.string,
    onClick: PropTypes.func
}

BannerCarousel.displayName = 'Banner Carousel'

export default BannerCarousel
